import { window } from '../../../../../globals';
import { fileUpload, put } from '../../../../../redux/api';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';
import type {
  UpdateOrganizationDefaultReportCustomTextRequest,
  UpdateOrganizationDefaultReportCustomTextResponse,
} from '../../../../../../typings/api';
import type { ReportExclusionOptions, ReportExclusionParams, ReportOptions } from '../../shared/types';

import type { IncludeFilterOption, IncludeFixPlanFilterOption, ReportLogoAction } from './types';
/**
 * Take the 'includes' choices from a V2 Report Generation preview and make their canonical URL params
 */
export function buildIncludeParams(includeOptions: IncludeFilterOption[]): ReportOptions {
  return Object.fromEntries(includeOptions.map((option) => [option, true]));
}

/**
 * Take the 'excludes' choices from a V2 Report Generation preview and make their canonical URL params
 * in the form ?excludeFields[packageLabels][0]=<label1>&excludeFields[packageLabels][1]=<label2>...
 */
export function buildExcludeParams(excludeOptions: ReportExclusionOptions): ReportExclusionParams {
  const params: ReportExclusionParams = { excludeFields: {} };
  if (excludeOptions.packageLabels) {
    params.excludeFields = {
      packageLabels: excludeOptions.packageLabels,
    };
  }
  return params;
}

/**
 * Take the 'includes' options for Fix Plan report and translate them into 'excludes' URL params
 */
export function buildFixPlanParams(
  includeOptions: IncludeFixPlanFilterOption[],
): FixPlansParams {
  const params: FixPlansParams = {};

  // if includeX is not in the list of options, put excludeX into the params
  if (!includeOptions.includes('includeQuickWins')) {
    params.excludeQuickWins = true;
  }

  if (!includeOptions.includes('includeHighPriority')) {
    params.excludeHighPriority = true;
  }

  if (!includeOptions.includes('includeLowPriority')) {
    params.excludeLowPriority = true;
  }

  if (!includeOptions.includes('includeOutdatedDependencies')) {
    params.excludeOutdatedDependencies = true;
  }

  if (includeOptions.includes('includeMalware')) {
    params.includeMalware = true;
  }

  if (includeOptions.includes('includeTransitiveVulns')) {
    params.includeTransitiveVulns = true;
  }

  if (includeOptions.includes('deduplicateOutdatedDeps')) {
    params.deduplicateOutdatedDeps = true;
  }

  return params;
}

/**
 * Update the report settings (upload/remove log and set custom text)
 */
export async function updateReportSettings({
  shouldSetDefault,
  file,
  reportLogoAction,
  shouldPropagate,
  customText,
  updateReportCustomTextEndpoint,
}: {
  shouldSetDefault: boolean;
  file: File | null;
  reportLogoAction: ReportLogoAction | null;
  customText: string;
  shouldPropagate: boolean;
  updateReportCustomTextEndpoint: string;
}): Promise<void> {
  const organizationId = window.user?.organization?.id;
  if (reportLogoAction === 'SAVE' && file) {
    await fileUpload(`/api/organizations/${organizationId}/logo/upload`, file, 'logo');
    if (window.user && window.user.organization) {
      window.user.organization.image = file.name;
    }
  }

  if (reportLogoAction === 'REMOVE') {
    // For now, we'll need to implement a proper remove endpoint
    // This is a placeholder - the new streaming approach doesn't have a remove endpoint yet
    throw new Error('Logo removal not yet implemented with new streaming endpoints');
  }

  if (shouldSetDefault) {
    await put<UpdateOrganizationDefaultReportCustomTextRequest, UpdateOrganizationDefaultReportCustomTextResponse>(
      `/api/organizations/${organizationId}/defaultReportCustomText`,
      {
        shouldPropagate,
        reportCustomText: customText,
      },
    );
  }
  await put(updateReportCustomTextEndpoint, { reportCustomText: customText });
}
